import React, { useEffect, useState } from 'react'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2, Plus, Trash2, Mail } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import SupportEndpoints from '@/endpoints/SupportEndpoints'
import { SupportTicketRecipient } from '@/model/SupportTicket'
import { DateTime } from 'luxon'

export const SupportRecipientsPage: React.FC = () => {
    const [recipients, setRecipients] = useState<SupportTicketRecipient[]>([])
    const [loading, setLoading] = useState(true)
    const [adding, setAdding] = useState(false)
    const [newEmail, setNewEmail] = useState('')

    const load = async () => {
        setLoading(true)
        try {
            const list = await SupportEndpoints.listRecipients()
            setRecipients(list)
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to load recipients'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault()
        const trimmed = newEmail.trim()
        if (!trimmed) {
            toast.error('Enter an email address.')
            return
        }
        setAdding(true)
        try {
            await SupportEndpoints.addRecipient(trimmed)
            setNewEmail('')
            await load()
            toast.success('Recipient added.')
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to add recipient'))
        } finally {
            setAdding(false)
        }
    }

    const handleRemove = async (id: number, email: string) => {
        if (!window.confirm(`Remove ${email} from the support recipient list?`)) {
            return
        }
        try {
            await SupportEndpoints.removeRecipient(id)
            setRecipients((prev) => prev.filter((r) => r.id !== id))
            toast.success('Recipient removed.')
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to remove recipient'))
        }
    }

    return (
        <div className="container mx-auto max-w-2xl px-4 py-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Support Ticket Recipients
                    </CardTitle>
                    <CardDescription>
                        Emails on this list receive a notification whenever a
                        user submits a support ticket through the "Report an
                        issue" form.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <form
                        onSubmit={handleAdd}
                        className="flex flex-col gap-2 sm:flex-row sm:items-end"
                    >
                        <div className="flex-1 space-y-2">
                            <Label htmlFor="recipient-email">Add email</Label>
                            <Input
                                id="recipient-email"
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="someone@example.com"
                                disabled={adding}
                            />
                        </div>
                        <Button type="submit" disabled={adding}>
                            {adding ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                <>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">
                            Current recipients
                        </Label>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : recipients.length === 0 ? (
                            <p className="rounded-md border bg-muted/30 px-3 py-4 text-sm italic text-muted-foreground">
                                No recipients yet. Tickets are still saved, but
                                no one will be notified until you add at least
                                one email here.
                            </p>
                        ) : (
                            <ul className="divide-y rounded-md border">
                                {recipients.map((r) => (
                                    <li
                                        key={r.id}
                                        className="flex items-center justify-between gap-3 px-3 py-2"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium">
                                                {r.email}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Added{' '}
                                                {DateTime.fromMillis(
                                                    Number(r.dateAdded)
                                                ).toFormat('MMM d, yyyy')}
                                                {r.addedBy
                                                    ? ` by ${r.addedBy.firstName} ${r.addedBy.lastName}`
                                                    : ''}
                                            </p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() =>
                                                handleRemove(r.id, r.email)
                                            }
                                            className="text-destructive hover:text-destructive"
                                            aria-label={`Remove ${r.email}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
